import { isHttp4xx } from '@app/utils/axiosUtils';
import { logAxiosError, throwException } from '@app/utils/exceptionHandling';
import { MongoAbility, createMongoAbility } from '@casl/ability';
import { Session } from '@ory/client';
import axios, { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs';

import { FlowParameters } from './authFlowInitialisationService';

const AUTH_SERVICE_URL =
  window.ENV.AUTH_SERVICE_URL ?? throwException(new Error('window.ENV.AUTH_SERVICE_URL not set'));

const API_URL = window.ENV.API_URL ?? throwException(new Error('window.ENV.API_URL not set'));

/**
 * 'Caching' the session helps not having to make repeated
 * calls to /sessions/whoami every time the user navigates to
 * a different page in the CMS.
 */
let session: Session | null = null;
let ability: MongoAbility | null = null;

const ERROR_SESSION_EXPIRED = 'Session expired';
const ERROR_UNAUTHORIZED = 'Unauthorized';
const ERROR_INVALID_LOGIN = 'Invalid login';

async function login(flowParameters: FlowParameters, username: string, password: string): Promise<Session> {
  const params = {
    csrf_token: flowParameters.csrfToken,
    identifier: username,
    password: password,
    method: 'password',
  };

  try {
    const response = await axios.post(
      `${AUTH_SERVICE_URL}/self-service/login?flow=${flowParameters.flowId}`,
      qs.stringify(params),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        withCredentials: true,
      }
    );
    return response.data['session'];
  } catch (error) {
    if ((error as AxiosError).isAxiosError) {
      let axiosError = error as AxiosError;
      logAxiosError('Failure making HTTP request to initialise login flow:', axiosError);
      if (isHttp4xx(axiosError)) {
        throw new Error(ERROR_INVALID_LOGIN);
      }
    }
    throw error;
  }
}

async function getUserSession(): Promise<Session> {
  if (!session) {
    console.log('Do not have session in memory or session expired, attempting to get new session...');

    try {
      const response: AxiosResponse<Session> = await axios.get(`${AUTH_SERVICE_URL}/sessions/whoami`, {
        withCredentials: true,
      });
      session = response.data;
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response && error.response.status === 401) {
        throw new Error(ERROR_UNAUTHORIZED);
      }
      throw new Error('Getting session from API failed');
    }
  }
  if (!session.expires_at) {
    throw new Error('Invalid session in memory');
  }
  if (sessionExpired(new Date(session.expires_at))) {
    console.log('Session expired');
    throw new Error(ERROR_SESSION_EXPIRED);
  }

  return session;
}

/**
 * Only to be used when we know for sure that
 * the user has logged in and has a valid session e.g.
 * AFTER navigating to any page, and NOT as part of
 * an action on an existing page
 */
function getUserEmail(): string | null {
  if (session) {
    // User always has at least one email
    return session.identity.verifiable_addresses![0].value;
  }
  return null;
}

function saveSession(newSession: Session | null) {
  session = newSession;
}

function deleteSession() {
  session = null;
}

function saveAbility(newAbility: MongoAbility | null) {
  ability = newAbility;
}

function deleteAbility() {
  ability = null;
}

async function getAbility(): Promise<MongoAbility> {
  console.log('Getting ability...');
  if (!ability) {
    let response = await axios.get(`${API_URL}/users/ability`, {
      headers: {
        Accept: 'application/json',
      },
      withCredentials: true,
    });
    ability = createMongoAbility(response.data);
  }
  return ability;
}

async function logout(): Promise<boolean> {
  let response = await axios.get(`${AUTH_SERVICE_URL}/self-service/logout/browser`, {
    headers: {
      Accept: 'application/json',
    },
    withCredentials: true,
  });
  const logoutUrl = `${AUTH_SERVICE_URL}/self-service/logout?token=${response.data['logout_token']}`;
  response = await axios.get(logoutUrl, { headers: { Accept: 'application/json' }, withCredentials: true });
  return true;
}

function sessionExpired(expiryTime: Date): boolean {
  const currentDateTime = new Date();
  return currentDateTime > expiryTime;
}

const authService = {
  login,
  logout,
  getUserSession,
  getUserEmail,
  getAbility,
  saveAbility,
  saveSession,
  deleteSession,
  deleteAbility: deleteAbility,
  ERROR_SESSION_EXPIRED,
  ERROR_UNAUTHORIZED,
  ERROR_INVALID_LOGIN,
};

export default authService;
